import { useEffect, useState } from 'react';
import {
  ERROR_MESSAGE_WEATHER_MODULE__500,
  ERROR_MESSAGE_WEATHER_MOON_PHASES__204,
} from '../../../../configurations/error-messages';
import { callEndpoint, telemetry } from '../../../../services/backend';
import {
  formatWeatherForecastMoonPhases,
  type WeatherForecastMoonPhaseDay,
  type WeatherMoonPhasesApiResponse,
} from '../helpers/moon/formatMoonPhases';
import { FREE_FORECAST_DAYS } from '../helpers/forecastHorizon';

export interface WeatherForecastMoonPhasesError {
  status?: number;
  type?: 'warning';
  message: string;
}

const hasValidCoordinates = (
  latitude: number | undefined,
  longitude: number | undefined,
) =>
  latitude != null &&
  longitude != null &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude);

export const useWeatherForecastMoonPhases = (
  latitude: number | undefined,
  longitude: number | undefined,
  days = FREE_FORECAST_DAYS,
) => {
  const [moonPhaseDays, setMoonPhaseDays] = useState<
    WeatherForecastMoonPhaseDay[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<WeatherForecastMoonPhasesError | null>(
    null,
  );

  useEffect(() => {
    let isMounted = true;

    if (!hasValidCoordinates(latitude, longitude)) {
      setMoonPhaseDays([]);
      setError(null);
      setIsLoading(true);
      return () => {
        isMounted = false;
      };
    }

    const loadMoonPhases = async () => {
      setIsLoading(true);
      setError(null);
      setMoonPhaseDays([]);

      try {
        const queryParams = {
          latitude: latitude as number,
          longitude: longitude as number,
          days,
        };

        const moonPhasesData = (await callEndpoint(
          callEndpoint.endpoints.queryWeatherMoonPhases,
          {},
          queryParams,
        )) as WeatherMoonPhasesApiResponse;

        if (!isMounted) {
          return;
        }

        if (
          moonPhasesData.constructor === Object &&
          Object.entries(moonPhasesData).length === 0
        ) {
          setError({
            status: 204,
            type: 'warning',
            message: ERROR_MESSAGE_WEATHER_MOON_PHASES__204,
          });
          return;
        }

        const formattedMoonPhaseDays = formatWeatherForecastMoonPhases(
          moonPhasesData,
          days,
        );

        if (!formattedMoonPhaseDays.length) {
          setError({
            status: 204,
            type: 'warning',
            message: ERROR_MESSAGE_WEATHER_MOON_PHASES__204,
          });
          return;
        }

        setMoonPhaseDays(formattedMoonPhaseDays);
      } catch (loadError) {
        telemetry(
          telemetry.ERROR,
          `[useWeatherForecastMoonPhases.loadMoonPhases] ${JSON.stringify(loadError)}`,
        );

        if (isMounted) {
          setError({
            message: ERROR_MESSAGE_WEATHER_MODULE__500,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMoonPhases();

    return () => {
      isMounted = false;
    };
  }, [days, latitude, longitude]);

  return {
    moonPhaseDays,
    isLoading,
    error,
  };
};
